import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import {
  NbAuthComponent,
} from '@nebular/auth';
import { DashboardLayoutComponent } from './components/dashboard-layout/dashboard-layout.component';
import { AuthGuard } from './guards/auth.guard';
import { accessGuard, AdminGuard, CreditAnalyticsGuard, DozentGuard, SuperAdminGuard } from './guards/role.guard';
import { UserAccess } from './services/auth.service';
import { ConnectionsComponent } from './pages/admin/connections/connections.component';
import { OrganisationsComponent } from './pages/admin/organisations/organisations.component';
import { AuditComponent } from './pages/admin/audit/audit.component';
import { ReferralsComponent } from './pages/admin/referrals/referrals.component';
import { LearningBlockCasesComponent } from './pages/admin/learning-block-cases/learning-block-cases.component';
import { PricingCalculatorComponent } from './pages/admin/pricing-calculator/pricing-calculator.component';
import { UsersComponent } from './pages/admin/users/users.component';
import { AdminLearnersComponent } from './pages/admin/learners/learners.component';
import { CourseAnalyticsComponent } from './pages/analytics/course-analytics/course-analytics.component';
import { LearnersAnalyticsComponent } from './pages/analytics/learners-analytics/learners-analytics.component';
import { ConfirmRegistrationComponent } from './pages/auth/confirm-registration/confirm-registration.component';
import { PasswordResetComponent } from './pages/auth/password-reset/password-reset.component';
import { PasswordRequestComponent } from './pages/auth/password-request/password-request.component';
import { ConfirmEmailChangeComponent } from './pages/auth/confirm-email-change/confirm-email-change.component';
import { NbLoginComponent } from './pages/auth/login/login.component';
import { TwoFactorSetupComponent } from './pages/auth/two-factor-setup/two-factor-setup.component';
import { NbRegisterComponent } from './pages/auth/register/register.component';
import { TemrsAndConditionsComponent } from './pages/auth/temrs-and-conditions/temrs-and-conditions.component';
import { MoodleSetupGuideComponent } from './pages/auth/moodle-setup-guide/moodle-setup-guide.component';
import { ChatStylingComponent } from './pages/chat-styling/chat-styling.component';
import { AcademyHomeComponent } from './pages/academy/academy-home/academy-home.component';
import { AcademyTopicComponent } from './pages/academy/academy-topic/academy-topic.component';
import { AcademyLessonComponent } from './pages/academy/academy-lesson/academy-lesson.component';
import { AcademyCertificateComponent } from './pages/academy/academy-certificate/academy-certificate.component';
import { CourseIdComponent } from './pages/courses/course-id/course-id.component';
import { LessionsIdComponent } from './pages/courses/course-id/lessions/lessions-id/lessions-id.component';
import { CoursesComponent } from './pages/courses/courses.component';
import { CreatorStartComponent } from './pages/creator/creator-start/creator-start.component';
import { CreatorLibraryComponent } from './pages/creator/creator-library/creator-library.component';
import { CurriculumComponent } from './pages/curriculum/curriculum.component';
import { DozentExercisesComponent } from './pages/dozent/exercises/exercises.component';
import { ExerciseEditorComponent } from './pages/dozent/exercises/exercise-editor/exercise-editor.component';
import { DozentFaqsComponent } from './pages/dozent/faqs/faqs.component';
import { DozentMessagesComponent } from './pages/dozent/messages/messages.component';
import { DozentenComponent } from './pages/dozent/dozenten/dozenten.component';
import { DozentParticipantsComponent } from './pages/dozent/participants/participants.component';
import { IntegrationsComponent } from './pages/integrations/integrations.component';
import { KnowledgeBaseComponent } from './pages/knowledge-base/knowledge-base.component';
import { NotFoundComponent } from './pages/not-found/not-found.component';
import { OrganisationComponent } from './pages/organisation/organisation.component';
import { McpConnectorDetailComponent } from './pages/mcp-connectors/mcp-connector-detail/mcp-connector-detail.component';
import { SkillsComponent } from './pages/skills/skills.component';
import { SkillEditorComponent } from './pages/skills/skill-editor/skill-editor.component';
import { AgentSetupComponent } from './pages/agent-setup/agent-setup.component';
import { TutorSetupComponent } from './pages/tutors/tutor-id/setup/tutor-setup.component';
import { TutorKnowledgeComponent } from './pages/tutors/tutor-id/knowledge/tutor-knowledge.component';
import { TutorLearningComponent } from './pages/tutors/tutor-id/learning/tutor-learning.component';
import { SECTIONS_ELSEWHERE } from './pages/tutors/tutor-id/ai-settings/ai-settings.sections';
import { TokenAnalyticsComponent } from './pages/token-analytics/token-analytics.component';
import { CreditAnalyticsComponent } from './pages/credit-analytics/credit-analytics.component';
import { ProductsComponent } from './pages/products/products.component';
import { SettingsComponent } from './pages/settings/settings.component';
import { AiSettingsComponent } from './pages/tutors/tutor-id/ai-settings/ai-settings.component';
import { OauthConsentComponent } from './pages/auth/oauth-consent/oauth-consent.component';
import { TutorShellComponent } from './pages/tutors/tutor-id/tutor-shell.component';
import { AccessComponent } from './pages/tutors/tutor-id/access/access.component';
import { LinkedTutorsComponent } from './pages/tutors/tutor-id/linked-tutors/linked-tutors.component';
import { GeneralComponent } from './pages/tutors/tutor-id/general/general.component';
import { TutorsComponent } from './pages/tutors/tutors.component';
import { PlacementsOverviewComponent } from './pages/tutors/placements/placements-overview.component';
import { LmsRegistrationsComponent } from './pages/tutors/lms-registrations/lms-registrations.component';
import { MoodleDashboardComponent } from './pages/moodle-dashboard/moodle-dashboard.component';
import { IliasDashboardComponent } from './pages/ilias-dashboard/ilias-dashboard.component';
import { UsageComponent } from './pages/usage/usage.component';

import { EventsComponent } from './pages/analytics/events/events.component';
import { LiveComponent } from './pages/analytics/live/live.component';
import { ConversationsComponent } from './pages/analytics/conversations/conversations.component';
import { SemanticsComponent } from './pages/analytics/semantics/semantics.component';
import { SessionsComponent } from './pages/analytics/sessions/sessions.component';


const routes: Routes = [
  {
    path: '',
    redirectTo: 'auth/login',
    pathMatch: 'full',
  },

  {
    path: '',
    component: DashboardLayoutComponent,
    canActivate: [AuthGuard],
    children: [
      {
        path: 'knowledge-base',
        component: KnowledgeBaseComponent,
        data: { agent: { page: 'knowledge.base' } },
      },
      {
        path: 'settings',
        component: SettingsComponent,
        data: { agent: { page: 'account.settings' } },
      },
      {
        path: 'tutors',
        component: TutorsComponent,
        data: { agent: { page: 'agents.list' } },
      },
      {
        path: 'tutors/placements',
        component: PlacementsOverviewComponent,
        data: { agent: { page: 'agents.placements' } },
      },
      {
        path: 'tutors/lms-registrations',
        component: LmsRegistrationsComponent,
        data: { agent: { page: 'lms.registrations' } },
      },
      {
        // Agent shell: frame + menu once, the pages below swap inside it.
        path: 'tutors/:tutorId',
        component: TutorShellComponent,
        children: [
          {
            path: 'general',
            component: GeneralComponent,
            data: { agent: { page: 'agent.general' } },
          },
          {
            path: 'ai-settings',
            component: AiSettingsComponent,
            data: { agent: { page: 'agent.ai-settings' } },
          },
          // Abschnitte, die jetzt Reiter anderer Seiten sind (Wissen,
          // Lernprozess, Verhalten & Fähigkeiten) — vor `ai-settings/:section`,
          // sonst gewönne der.
          ...Object.entries(SECTIONS_ELSEWHERE).map(([section, target]) => ({
            path: `ai-settings/${section}`,
            redirectTo: target,
            pathMatch: 'full' as const,
          })),
          {
            // Per-section deep-link so the tutor-menu sub-entries map to
            // unique URLs and Nebular's nb-menu can highlight them by path.
            path: 'ai-settings/:section',
            component: AiSettingsComponent,
            data: { agent: { page: 'agent.ai-settings' } },
          },
          {
            // Agent › Wissen: Wissensbasis und „Quellen & Suche" als Reiter,
            // eine URL je Reiter (wie „Verhalten & Fähigkeiten").
            path: 'knowledge/:tab',
            component: TutorKnowledgeComponent,
            data: { agent: { page: 'agent.knowledge' } },
          },
          { path: 'knowledge', redirectTo: 'knowledge/base', pathMatch: 'full' },
          {
            // Agent › Lernprozess: Didaktik, Dozent, Lern-Interface,
            // Lernerprofil, Curriculum als Reiter, eine URL je Reiter.
            path: 'learning/:tab',
            component: TutorLearningComponent,
            data: { agent: { page: 'agent.learning' } },
          },
          { path: 'learning', redirectTo: 'learning/didaktik', pathMatch: 'full' },
          // Die frühere eigene Seite — Links und Lesezeichen landen im Reiter.
          { path: 'knowledge-base', redirectTo: 'knowledge/base', pathMatch: 'full' },
          {
            // "Verhalten & Fähigkeiten" for one agent: Learnings, Skills,
            // MCP connectors, Regeln & Grenzen as tabs, one URL per tab.
            path: 'setup/:tab',
            component: TutorSetupComponent,
            data: { agent: { page: 'agent.setup' } },
          },
          { path: 'setup', redirectTo: 'setup/learnings', pathMatch: 'full' },
          // The former single pages — links and bookmarks land on the tab.
          { path: 'learnings', redirectTo: 'setup/learnings', pathMatch: 'full' },
          { path: 'skills', redirectTo: 'setup/skills', pathMatch: 'full' },
          { path: 'mcp', redirectTo: 'setup/mcp', pathMatch: 'full' },
          { path: 'guardrails', redirectTo: 'setup/guardrails', pathMatch: 'full' },
          {
            // Admin/manager-only access management. The tutor-menu hides the
            // entry for other roles and the backend rejects the grant endpoints,
            // so a non-admin reaching this URL directly sees only a load error.
            path: 'access',
            component: AccessComponent,
            data: { agent: { page: 'agent.access' } },
          },
          {
            // Only meaningful for alpha_dozent bots — the tutor-menu
            // hides the sub-entry for other templates so unlikely to
            // be reached otherwise.
            path: 'linked-tutors',
            component: LinkedTutorsComponent,
            data: { agent: { page: 'agent.linked-tutors' } },
          },
        ],
      },
      // Academy — Lern- und Hilfezentrum für alle Portal-Nutzer (Inhalte:
      // academy/academy-catalog.ts, Fortschritt: al-tutor /academy/progress).
      {
        path: 'academy',
        component: AcademyHomeComponent,
        data: { agent: { page: 'academy.home' } },
      },
      {
        path: 'academy/topics/:topicId',
        component: AcademyTopicComponent,
        data: { agent: { page: 'academy.topic' } },
      },
      {
        path: 'academy/lessons/:lessonId',
        component: AcademyLessonComponent,
        data: { agent: { page: 'academy.lesson' } },
      },
      {
        path: 'academy/certificates/:certificateId',
        component: AcademyCertificateComponent,
        data: { agent: { page: 'academy.certificate' } },
      },
      {
        path: 'chat-styling',
        component: ChatStylingComponent,
        data: { agent: { page: 'chat-styling' } },
      },
      {
        path: 'usage',
        component: UsageComponent,
        data: { agent: { page: 'org.usage' } },
      },
      {
        path: 'integrations',
        component: IntegrationsComponent,
        data: { agent: { page: 'integrations.overview' } },
      },
      {
        path: 'integrations/lti',
        component: LmsRegistrationsComponent,
        data: { agent: { page: 'lms.registrations' } },
      },
      {
        // Org-feature opt-in via clients.features ⊇ 'MOODLE_DASHBOARD'.
        // Sidebar visibility is gated in dashboard-layout.component.ts;
        // the backend canonical endpoints additionally enforce
        // PRIVILEGED_ROLES.
        path: 'moodle',
        component: MoodleDashboardComponent,
        data: { agent: { page: 'integrations.moodle' } },
      },
      {
        // ILIAS-Uebersicht. Getrennt von den Platzierungen: hier steht, was in
        // ILIAS existiert, dort wird ein Agent zugeordnet. Die Endpunkte
        // erzwingen zusaetzlich PRIVILEGED_ROLES.
        path: 'ilias',
        component: IliasDashboardComponent,
        data: { agent: { page: 'integrations.ilias' } },
      },
      {
        path: 'products',
        component: ProductsComponent,
        data: { agent: { page: 'org.products' } },
      },
      {
        path: 'settings',
        component: SettingsComponent,
        data: { agent: { page: 'account.settings' } },
      },
      {
        path: 'organisation',
        component: OrganisationComponent,
        data: { agent: { page: 'org.organisation' } },
        // Same predicate the sidebar uses. Without it a signed-in `user`
        // could type the URL and get the page rendered — no data leaked (the
        // endpoints scope server-side) but a wall of empty and failing tiles.
        canActivate: [accessGuard(UserAccess.UserMenuOrganisation)],
      },
      {
        // KI-Agenten › Verhalten & Fähigkeiten: Learnings, Skills (admin),
        // MCP connectors (admin), Regeln & Grenzen as tabs, one URL per tab.
        path: 'agent-setup/:tab',
        component: AgentSetupComponent,
        data: { agent: { page: 'agents.setup' } },
      },
      { path: 'agent-setup', redirectTo: 'agent-setup/learnings', pathMatch: 'full' },
      // The former single pages — links and bookmarks land on the tab.
      { path: 'learnings', redirectTo: 'agent-setup/learnings', pathMatch: 'full' },
      { path: 'guardrails', redirectTo: 'agent-setup/guardrails', pathMatch: 'full' },
      { path: 'skills', redirectTo: 'agent-setup/skills', pathMatch: 'full' },
      { path: 'mcp-connectors', redirectTo: 'agent-setup/mcp', pathMatch: 'full' },
      {
        path: 'mcp-connectors/:connectorId',
        component: McpConnectorDetailComponent,
        data: { agent: { page: 'agents.mcp-connector' } },
      },
      {
        // `new` or a skill id
        path: 'skills/:skillId',
        component: SkillEditorComponent,
        data: { agent: { page: 'agents.skill-editor' } },
      },
      {
        path: 'creator',
        component: CreatorStartComponent,
        data: { agent: { page: 'content.creator' } },
      },
      {
        // the start page with the course dialog open
        path: 'creator/new-course',
        component: CreatorStartComponent,
        data: { open: 'course', agent: { page: 'content.creator-new-course' } },
      },
      {
        path: 'creator/library',
        component: CreatorLibraryComponent,
        data: { agent: { page: 'content.creator-library' } },
      },
      {
        // the canvas (AI chat and editors) in its frame
        path: 'creator/workspace',
        component: CoursesComponent,
        data: { agent: { page: 'content.creator-workspace' } },
      },
      {
        path: 'curriculum',
        component: CurriculumComponent,
        data: { agent: { page: 'content.curriculum' } },
      },
      {
        path: 'courses/:courseId',
        component: CourseIdComponent,
        data: { agent: { page: 'content.course' } },
      },
      {
        path: 'courses/:courseId/lessons/:lessonId',
        component: LessionsIdComponent,
        data: { agent: { page: 'content.lesson' } },
      },
      {
        path: 'analytics/courses',
        component: CourseAnalyticsComponent,
        data: { agent: { page: 'analytics.courses' } },
      },
      {
        path: 'analytics/learners',
        component: LearnersAnalyticsComponent,
        data: { agent: { page: 'analytics.learners' } },
      },
      {
        path: 'analytics/events',
        component: EventsComponent,
        data: { agent: { page: 'analytics.events' } },
      },
      {
        path: 'analytics/semantics',
        component: SemanticsComponent,
        data: { agent: { page: 'analytics.semantics' } },
      },
      {
        path: 'analytics/conversations',
        component: ConversationsComponent,
        data: { agent: { page: 'analytics.conversations' } },
      },
      {
        path: 'analytics/live',
        component: LiveComponent,
        data: { agent: { page: 'analytics.live' } },
      },
      {
        path: 'analytics/sessions',
        component: SessionsComponent,
        data: { agent: { page: 'analytics.sessions' } },
      },
      {
        // Credit-Verbrauch des eigenen Mandanten. Vorerst nur super_admin —
        // Freigabe fuer Kunden-Admins in `credit-analytics.access.ts`.
        path: 'analytics/credits',
        component: CreditAnalyticsComponent,
        data: { agent: { page: 'analytics.credits' } },
        canActivate: [CreditAnalyticsGuard],
      },
      // Dozent-specific routes
      {
        path: 'dozent/exercises',
        canActivate: [DozentGuard],
        component: DozentExercisesComponent,
        data: { agent: { page: 'dozent.exercises' } },
      },
      {
        path: 'dozent/exercises/:id',
        canActivate: [DozentGuard],
        component: ExerciseEditorComponent,
        data: { agent: { page: 'dozent.exercise-editor' } },
      },
      {
        path: 'dozent/participants',
        canActivate: [DozentGuard],
        component: DozentParticipantsComponent,
        data: { agent: { page: 'dozent.participants' } },
      },
      {
        path: 'dozent/faqs',
        canActivate: [DozentGuard],
        component: DozentFaqsComponent,
        data: { agent: { page: 'dozent.faqs' } },
      },
      {
        path: 'dozent/messages',
        canActivate: [DozentGuard],
        component: DozentMessagesComponent,
        data: { agent: { page: 'dozent.messages' } },
      },
      {
        path: 'dozent/dozenten',
        canActivate: [DozentGuard],
        component: DozentenComponent,
        data: { agent: { page: 'dozent.dozenten' } },
      },
      {
        path: 'analytics/dozent-students',
        canActivate: [DozentGuard],
        component: TutorsComponent, // placeholder until analytics page is built
        data: { agent: { page: 'analytics.dozent-students' } },
      },
    ],
  },

  {
    path: 'admin',
    component: DashboardLayoutComponent,
    canActivate: [AuthGuard, AdminGuard],
    children: [
      {
        path: 'users',
        component: UsersComponent,
        data: { agent: { page: 'admin.users' } },
      },
      {
        path: 'learners',
        component: AdminLearnersComponent,
        data: { agent: { page: 'admin.learners' } },
      },
      {
        path: 'organisations',
        component: OrganisationsComponent,
        data: { agent: { page: 'admin.organisations' } },
      },
      {
        path: 'connections',
        component: ConnectionsComponent,
        data: { agent: { page: 'admin.connections' } },
      },
      {
        path: 'audit',
        component: AuditComponent,
        data: { agent: { page: 'admin.audit' } },
      },
      {
        // Org-wide LangSmith token economy — platform-level data,
        // therefore super_admin only (stricter than the parent AdminGuard).
        path: 'token-analytics',
        component: TokenAnalyticsComponent,
        data: { agent: { page: 'admin.token-analytics' } },
        canActivate: [SuperAdminGuard],
      },
      {
        // Partner revenue share (lern.link) — platform money, super_admin only.
        path: 'referrals',
        component: ReferralsComponent,
        data: { agent: { page: 'admin.referrals' } },
        canActivate: [SuperAdminGuard],
      },
      {
        // Learning cases for the chat's interactive learning aids — platform-
        // wide, they steer every agent: super_admin only.
        path: 'learning-block-cases',
        component: LearningBlockCasesComponent,
        data: { agent: { page: 'admin.learning-block-cases' } },
        canActivate: [SuperAdminGuard],
      },
      {
        // Angebote & Preise: interner Preisrechner für Angebote — super_admin only.
        path: 'pricing',
        component: PricingCalculatorComponent,
        data: { agent: { page: 'admin.pricing' } },
        canActivate: [SuperAdminGuard],
      },
    ],
  },

  {
    path: 'auth',
    component: NbAuthComponent,
    children: [
      {
        path: '',
        pathMatch: 'full',
        redirectTo: 'login',
      },
      {
        path: 'login',
        component: NbLoginComponent,
      },
      {
        path: 'register',
        component: NbRegisterComponent,
      },
      {
        path: 'request-password',
        component: PasswordRequestComponent,
      },
      {
        path: 'password-reset',
        component: PasswordResetComponent,
      },
      {
        path: 'confirm-registration',
        component: ConfirmRegistrationComponent,
      },
      {
        path: 'confirm-email-change',
        component: ConfirmEmailChangeComponent,
      },
      {
        // Continuation of a half-finished sign-in, not a standalone page:
        // without the enrolment token the component bounces back to login.
        path: 'two-factor-setup',
        component: TwoFactorSetupComponent,
      },
      {
        path: 'terms-and-conditions',
        component: TemrsAndConditionsComponent,
      },
      {
        // An AI agent (Claude, Cursor …) asks to sign in: al-user-management
        // /oauth/authorize sends the browser here. Checks the login itself.
        path: 'oauth-consent',
        component: OauthConsentComponent,
      },
    ],
  },

  {
    path: 'moodle-setup-guide',
    component: MoodleSetupGuideComponent,
  },

  {
    path: '**',
    component: NotFoundComponent,
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
